import type {
  SocketClientMessage,
  SocketClientMessageData,
  SocketClientMessageType,
  SocketServerMessage,
} from '@arcexperts/core-types'
import { zod_SocketServerMessage } from '@arcexperts/core-types'

function isBuildingTypesMessage(value: unknown): value is SocketServerMessage<'building_types'> {
  if (!value || typeof value !== 'object') return false

  const message = value as Record<string, unknown>
  const data = message.data as Record<string, unknown> | undefined
  return message.type === 'building_types' && typeof message.timestamp === 'number' && Array.isArray(data?.items)
}

function hasExtendedGeneralFields(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false

  const message = value as Record<string, unknown>
  if (message.type !== 'default_data' && message.type !== 'inject_data') return false

  const data = message.data as Record<string, unknown> | undefined
  const project = data?.project as Record<string, unknown> | undefined
  const general = project?.general as Record<string, unknown> | undefined
  const generalData = general?.data as Record<string, unknown> | undefined
  const usageItems = generalData?.usageItems

  return Boolean(
    generalData &&
    ('landscapeQuantityArea' in generalData ||
      'landscapeUsageItems' in generalData ||
      'civilEngineerSlabArea' in generalData ||
      (Array.isArray(usageItems) &&
        usageItems.some(
          (item) =>
            item && typeof item === 'object' && ('aboveTerrainAreaRatio' in item || 'belowTerrainAreaRatio' in item)
        )))
  )
}

function createMessage<Type extends SocketClientMessageType>(type: Type, data: SocketClientMessageData<Type>): string {
  const message: SocketClientMessage<Type> = {
    type: type,
    data: data,
    timestamp: Date.now(),
  }
  // ! json stringify can not handle Map/Dictionary data !!!
  return JSON.stringify(message)
}

function getMessage(message: MessageEvent): SocketServerMessage {
  try {
    const parsedMessage = JSON.parse(message.data) as unknown
    if (isBuildingTypesMessage(parsedMessage) || hasExtendedGeneralFields(parsedMessage)) {
      return parsedMessage as SocketServerMessage
    }

    const result = zod_SocketServerMessage.safeParse(parsedMessage)

    if (result.success) {
      return result.data as SocketServerMessage
    }
  } catch {
    // Invalid JSON is handled with the same fallback as invalid envelopes.
  }

  return {
    type: 'string',
    timestamp: 0,
    data: String(message.data),
  }
}

export { createMessage, getMessage }
